import React from 'react';

import { ContentType } from '_utils/helpers/contentType';
import { pickAttributeByType, AttributeType, Attribute } from '_utils/helpers/attributeType';
import { getAttributeRenderer, getMultipleAttributeRenderer } from './getAttributeRenderer';
import { useTranslation } from 'react-i18next';

export * from './getAttributeRenderer';
export * from './definitions';

export type WrapperProps = {
  children: React.ReactNode;
};

type ContentTypeAttributesProps = {
  attributes: any[];
  contentType: ContentType;
  Wrapper: React.ComponentType<WrapperProps>;
};

const ContentTypeAttributes: React.FC<ContentTypeAttributesProps> = ({
  attributes,
  contentType,
  Wrapper
}) => {
  const [t] = useTranslation();

  let publicationLastUpdated = t('publication-related-content-last-updated-prefix');

  if (publicationLastUpdated === 'publication-related-content-last-updated-prefix') {
    publicationLastUpdated = '';
  }

  const translations = {
    publicationLastUpdated,
    capacitySingular: t('space-capacity-unit-singular'),
    capacityPlural: t('space-capacity-unit-plural')
  };

  let attributeRenderers: any[] = [];

  if (contentType === ContentType.project) {
    const statusName = pickAttributeByType(attributes, AttributeType.statusName);
    const statusIcon = pickAttributeByType(attributes, AttributeType.statusIcon);

    const projectRenderer = getMultipleAttributeRenderer({
      [AttributeType.statusName]: statusName,
      [AttributeType.statusIcon]: statusIcon
    });

    attributeRenderers.push(projectRenderer);
  }

  attributeRenderers.push(
    getAttributeRenderer(pickAttributeByType(attributes, AttributeType.location))
  );

  attributeRenderers.push(
    getAttributeRenderer(pickAttributeByType(attributes, AttributeType.capacity), {
      translations
    })
  );

  attributeRenderers.push(
    getAttributeRenderer(pickAttributeByType(attributes, AttributeType.whatsOnCost))
  );

  // lastUpdated can appear for other (unwanted) contentTypes - only display for publication
  if (contentType === ContentType.publication) {
    attributeRenderers.push(
      getAttributeRenderer(pickAttributeByType(attributes, AttributeType.lastUpdated), {
        translations
      })
    );
  }

  attributeRenderers.push(
    getAttributeRenderer(pickAttributeByType(attributes, AttributeType.newsDate))
  );

  if (contentType === ContentType.service) {
    const timeSpecificity: Attribute = pickAttributeByType(
      attributes,
      AttributeType.timeSpecificity
    );
    const timeUnit: Attribute = pickAttributeByType(attributes, AttributeType.timeUnit);
    const timeEstimate: Attribute = pickAttributeByType(attributes, AttributeType.timeEstimate);
    const costEstimate: Attribute = pickAttributeByType(attributes, AttributeType.costEstimate);
    const costSpecificity: Attribute = pickAttributeByType(
      attributes,
      AttributeType.costSpecificity
    );

    const timeRenderer = getMultipleAttributeRenderer({
      [AttributeType.timeSpecificity]: timeSpecificity,
      [AttributeType.timeUnit]: timeUnit,
      [AttributeType.timeEstimate]: timeEstimate
    });

    const costRenderer = getMultipleAttributeRenderer(
      {
        [AttributeType.costEstimate]: costEstimate,
        [AttributeType.costSpecificity]: costSpecificity
      },
      { showIcon: timeRenderer !== null }
    );

    attributeRenderers.push(timeRenderer, costRenderer);
  }

  const renderings = attributeRenderers
    .map((renderer) => (typeof renderer === 'function' ? renderer() : null))
    .filter(Boolean);

  if (renderings.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      {renderings.map((rendering, index) => (
        <React.Fragment key={index}>
          {index > 0 && <span role="presentation"> · </span>}
          {rendering}
        </React.Fragment>
      ))}
    </Wrapper>
  );
};

export default ContentTypeAttributes;
